// ============================================================================
// BOSHLANG'ICH KONTENT — restoran yaratilganda menyu, sklad va retseptlar
//
// Platforma admini "Shablondan" ni tanlasa, restoran bo'sh emas, ishlashga
// tayyor holda ochiladi: kategoriyalar, mahsulotlar, xom ashyo, retseptlar,
// qo'shimchalar. Ega keyin narx va nomlarni o'zgartiradi.
//
// Retsept miqdorlari xom ashyoning asosiy birligida (g / ml / dona).
// ============================================================================

import type { RestaurantTemplateKey } from './templates';

export type BaseUnit = 'g' | 'ml' | 'dona';

export interface StarterRawMaterial {
  code: string;
  name: string;
  category: string;
  baseUnit: BaseUnit;
  packName?: string;
  packSize?: number;
  minStock?: number;
}

/** Xom ashyo kodi -> sarf (asosiy birlikda) */
export type StarterRecipe = Readonly<Record<string, number>>;

export interface StarterModifier {
  name: string;
  price: number;
  isDefault?: boolean;
  recipe?: StarterRecipe;
}

export interface StarterModifierGroup {
  key: string;
  name: string;
  minSelect: number;
  maxSelect: number;
  modifiers: readonly StarterModifier[];
}

export interface StarterProduct {
  name: string;
  shortName?: string;
  price: number;
  /** Ichki bo'lim: bir xil mahsulotning o'lchamlari (Cola 0.5L, 1.5L) */
  group?: string;
  variant?: string;
  prepMinutes?: number;
  /** false = sklad hisobi yuritilmaydi */
  trackStock?: boolean;
  recipe?: StarterRecipe;
  modifierGroups?: readonly string[];
}

export interface StarterCategory {
  name: string;
  icon?: string;
  /** Shablondagi stansiya kaliti (oshxona, bar). Shablonda bo'lmasa — e'tiborsiz */
  station?: string;
  modifierGroups?: readonly string[];
  products: readonly StarterProduct[];
}

export interface StarterContent {
  key: string;
  label: string;
  rawMaterials: readonly StarterRawMaterial[];
  modifierGroups: readonly StarterModifierGroup[];
  categories: readonly StarterCategory[];
}

// ---------------------------------------------------------------------------
// FASTFOOD (lavash, shaurma, burger)
// ---------------------------------------------------------------------------
const FAST: StarterContent = {
  key: 'fast',
  label: 'Fastfood menyusi',
  rawMaterials: [
    { code: 'lavash_non', name: 'Lavash noni', category: 'non', baseUnit: 'dona', packName: 'qop', packSize: 50, minStock: 50 },
    { code: 'burger_non', name: 'Burger bulochkasi', category: 'non', baseUnit: 'dona', packName: 'quti', packSize: 24, minStock: 24 },
    { code: 'hotdog_non', name: 'Hot-dog bulochkasi', category: 'non', baseUnit: 'dona', packName: 'quti', packSize: 30, minStock: 30 },
    { code: 'mol_goshti', name: 'Mol go‘shti (tayyor)', category: 'go‘sht', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 5000 },
    { code: 'tovuq_goshti', name: 'Tovuq go‘shti (tayyor)', category: 'go‘sht', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 5000 },
    { code: 'burger_kotlet', name: 'Burger kotleti', category: 'go‘sht', baseUnit: 'dona', packName: 'quti', packSize: 20, minStock: 20 },
    { code: 'sosiska', name: 'Sosiska', category: 'go‘sht', baseUnit: 'dona', packName: 'pachka', packSize: 10, minStock: 20 },
    { code: 'kartoshka_fri', name: 'Kartoshka fri (muzlatilgan)', category: 'sabzavot', baseUnit: 'g', packName: 'qop', packSize: 2500, minStock: 5000 },
    { code: 'pomidor', name: 'Pomidor', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 2000 },
    { code: 'bodring', name: 'Bodring', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 2000 },
    { code: 'karam', name: 'Karam', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 2000 },
    { code: 'piyoz', name: 'Piyoz', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 1000 },
    { code: 'pishloq', name: 'Pishloq', category: 'sut', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 1000 },
    { code: 'mayonez', name: 'Mayonez', category: 'sous', baseUnit: 'g', packName: 'chelak', packSize: 5000, minStock: 2000 },
    { code: 'ketchup', name: 'Ketchup', category: 'sous', baseUnit: 'g', packName: 'chelak', packSize: 5000, minStock: 2000 },
    { code: 'achchiq_sous', name: 'Achchiq sous', category: 'sous', baseUnit: 'g', packName: 'banka', packSize: 1000, minStock: 500 },
    { code: 'yog', name: 'O‘simlik yog‘i', category: 'boshqa', baseUnit: 'ml', packName: 'litr', packSize: 1000, minStock: 5000 },
    { code: 'cola_05', name: 'Coca-Cola 0.5L', category: 'ichimlik', baseUnit: 'dona', packName: 'blok', packSize: 12, minStock: 24 },
    { code: 'cola_15', name: 'Coca-Cola 1.5L', category: 'ichimlik', baseUnit: 'dona', packName: 'blok', packSize: 6, minStock: 12 },
    { code: 'fanta_05', name: 'Fanta 0.5L', category: 'ichimlik', baseUnit: 'dona', packName: 'blok', packSize: 12, minStock: 24 },
    { code: 'suv_05', name: 'Suv 0.5L', category: 'ichimlik', baseUnit: 'dona', packName: 'blok', packSize: 12, minStock: 24 },
    { code: 'choy_quruq', name: 'Choy (quruq)', category: 'ichimlik', baseUnit: 'g', packName: 'pachka', packSize: 100, minStock: 300 },
    { code: 'stakan', name: 'Bir martalik stakan', category: 'qadoq', baseUnit: 'dona', packName: 'pachka', packSize: 50, minStock: 100 },
  ],
  modifierGroups: [
    {
      key: 'qoshimcha',
      name: 'Qo‘shimcha',
      minSelect: 0,
      maxSelect: 4,
      modifiers: [
        { name: 'Pishloq', price: 5000, recipe: { pishloq: 30 } },
        { name: 'Ketchup', price: 2000, recipe: { ketchup: 20 } },
        { name: 'Mayonez', price: 2000, recipe: { mayonez: 20 } },
        { name: 'Achchiq sous', price: 2000, recipe: { achchiq_sous: 15 } },
      ],
    },
  ],
  categories: [
    {
      name: 'Lavash',
      icon: '🌯',
      station: 'kitchen',
      modifierGroups: ['qoshimcha'],
      products: [
        {
          name: 'Lavash mol go‘shtli',
          shortName: 'Lavash mol',
          price: 35000,
          prepMinutes: 5,
          recipe: { lavash_non: 1, mol_goshti: 120, pomidor: 30, bodring: 30, karam: 20, kartoshka_fri: 40, mayonez: 20, ketchup: 10 },
        },
        {
          name: 'Lavash tovuqli',
          shortName: 'Lavash tovuq',
          price: 32000,
          prepMinutes: 5,
          recipe: { lavash_non: 1, tovuq_goshti: 120, pomidor: 30, bodring: 30, karam: 20, kartoshka_fri: 40, mayonez: 20, ketchup: 10 },
        },
        {
          name: 'Lavash pishloqli',
          shortName: 'Lavash pishloq',
          price: 39000,
          prepMinutes: 6,
          recipe: { lavash_non: 1, mol_goshti: 120, pishloq: 30, pomidor: 30, bodring: 30, karam: 20, mayonez: 20 },
        },
      ],
    },
    {
      name: 'Shaurma',
      icon: '🥙',
      station: 'kitchen',
      modifierGroups: ['qoshimcha'],
      products: [
        {
          name: 'Shaurma mol go‘shtli',
          shortName: 'Shaurma mol',
          price: 33000,
          prepMinutes: 5,
          recipe: { lavash_non: 1, mol_goshti: 100, pomidor: 30, bodring: 30, piyoz: 15, mayonez: 25 },
        },
        {
          name: 'Shaurma tovuqli',
          shortName: 'Shaurma tovuq',
          price: 30000,
          prepMinutes: 5,
          recipe: { lavash_non: 1, tovuq_goshti: 100, pomidor: 30, bodring: 30, piyoz: 15, mayonez: 25 },
        },
      ],
    },
    {
      name: 'Burger va hot-dog',
      icon: '🍔',
      station: 'kitchen',
      modifierGroups: ['qoshimcha'],
      products: [
        {
          name: 'Gamburger',
          price: 30000,
          prepMinutes: 6,
          recipe: { burger_non: 1, burger_kotlet: 1, pomidor: 25, bodring: 20, piyoz: 10, ketchup: 15, mayonez: 15 },
        },
        {
          name: 'Chizburger',
          price: 34000,
          prepMinutes: 6,
          recipe: { burger_non: 1, burger_kotlet: 1, pishloq: 25, pomidor: 25, bodring: 20, ketchup: 15, mayonez: 15 },
        },
        {
          name: 'Hot-dog',
          price: 18000,
          group: 'Hot-dog',
          variant: 'Oddiy',
          prepMinutes: 3,
          recipe: { hotdog_non: 1, sosiska: 1, ketchup: 15, mayonez: 15 },
        },
        {
          name: 'Hot-dog dubl',
          price: 24000,
          group: 'Hot-dog',
          variant: 'Dubl',
          prepMinutes: 3,
          recipe: { hotdog_non: 1, sosiska: 2, ketchup: 15, mayonez: 15 },
        },
      ],
    },
    {
      name: 'Garnir',
      icon: '🍟',
      station: 'kitchen',
      products: [
        { name: 'Kartoshka fri', price: 15000, group: 'Kartoshka fri', variant: 'O‘rta', prepMinutes: 4, recipe: { kartoshka_fri: 150, yog: 15 } },
        { name: 'Kartoshka fri katta', price: 20000, group: 'Kartoshka fri', variant: 'Katta', prepMinutes: 4, recipe: { kartoshka_fri: 220, yog: 20 } },
      ],
    },
    {
      name: 'Ichimliklar',
      icon: '🥤',
      products: [
        { name: 'Coca-Cola 0.5L', price: 8000, group: 'Coca-Cola', variant: '0.5L', prepMinutes: 0, recipe: { cola_05: 1 } },
        { name: 'Coca-Cola 1.5L', price: 15000, group: 'Coca-Cola', variant: '1.5L', prepMinutes: 0, recipe: { cola_15: 1 } },
        { name: 'Fanta 0.5L', price: 8000, prepMinutes: 0, recipe: { fanta_05: 1 } },
        { name: 'Suv 0.5L', price: 4000, prepMinutes: 0, recipe: { suv_05: 1 } },
        { name: 'Choy', price: 5000, prepMinutes: 1, recipe: { choy_quruq: 3, stakan: 1 } },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// KOFE VA DESERT
// ---------------------------------------------------------------------------
const COFFEE: StarterContent = {
  key: 'coffee',
  label: 'Kofe va desert menyusi',
  rawMaterials: [
    { code: 'kofe_don', name: 'Kofe doni', category: 'kofe', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 2000 },
    { code: 'sut', name: 'Sut', category: 'sut', baseUnit: 'ml', packName: 'litr', packSize: 1000, minStock: 10000 },
    { code: 'kokos_sut', name: 'Kokos suti', category: 'sut', baseUnit: 'ml', packName: 'litr', packSize: 1000, minStock: 2000 },
    { code: 'bodom_sut', name: 'Bodom suti', category: 'sut', baseUnit: 'ml', packName: 'litr', packSize: 1000, minStock: 2000 },
    { code: 'laktozasiz_sut', name: 'Laktozasiz sut', category: 'sut', baseUnit: 'ml', packName: 'litr', packSize: 1000, minStock: 2000 },
    { code: 'kakao', name: 'Kakao kukuni', category: 'kofe', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 500 },
    { code: 'muz', name: 'Muz', category: 'boshqa', baseUnit: 'g', packName: 'qop', packSize: 5000, minStock: 5000 },
    { code: 'qaymoq', name: 'Qaymoq (slivka)', category: 'sut', baseUnit: 'ml', packName: 'litr', packSize: 1000, minStock: 2000 },
    { code: 'shakar', name: 'Shakar', category: 'boshqa', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 2000 },
    { code: 'sirop_karamel', name: 'Sirop karamel', category: 'sirop', baseUnit: 'ml', packName: 'shisha', packSize: 1000, minStock: 500 },
    { code: 'sirop_vanil', name: 'Sirop vanil', category: 'sirop', baseUnit: 'ml', packName: 'shisha', packSize: 1000, minStock: 500 },
    { code: 'sirop_fundiq', name: 'Sirop fundiq', category: 'sirop', baseUnit: 'ml', packName: 'shisha', packSize: 1000, minStock: 500 },
    { code: 'choy_kok', name: 'Ko‘k choy', category: 'choy', baseUnit: 'g', packName: 'pachka', packSize: 100, minStock: 300 },
    { code: 'choy_qora', name: 'Qora choy', category: 'choy', baseUnit: 'g', packName: 'pachka', packSize: 100, minStock: 300 },
    { code: 'kruassan', name: 'Kruassan (tayyor)', category: 'desert', baseUnit: 'dona', minStock: 10 },
    { code: 'chizkeyk', name: 'Chizkeyk (bo‘lak)', category: 'desert', baseUnit: 'dona', minStock: 8 },
    { code: 'medovik', name: 'Medovik (bo‘lak)', category: 'desert', baseUnit: 'dona', minStock: 8 },
    { code: 'brauni', name: 'Brauni (bo‘lak)', category: 'desert', baseUnit: 'dona', minStock: 8 },
    { code: 'stakan_300', name: 'Stakan 300 ml', category: 'qadoq', baseUnit: 'dona', packName: 'pachka', packSize: 50, minStock: 100 },
    { code: 'stakan_400', name: 'Stakan 400 ml', category: 'qadoq', baseUnit: 'dona', packName: 'pachka', packSize: 50, minStock: 100 },
    { code: 'qopqoq', name: 'Stakan qopqog‘i', category: 'qadoq', baseUnit: 'dona', packName: 'pachka', packSize: 100, minStock: 200 },
  ],
  modifierGroups: [
    {
      key: 'sirop',
      name: 'Sirop',
      minSelect: 0,
      maxSelect: 2,
      modifiers: [
        { name: 'Karamel', price: 5000, recipe: { sirop_karamel: 15 } },
        { name: 'Vanil', price: 5000, recipe: { sirop_vanil: 15 } },
        { name: 'Fundiq', price: 5000, recipe: { sirop_fundiq: 15 } },
      ],
    },
    {
      key: 'sut_turi',
      name: 'Sut turi',
      minSelect: 0,
      maxSelect: 1,
      modifiers: [
        { name: 'Kokos suti', price: 7000, recipe: { kokos_sut: 150 } },
        { name: 'Bodom suti', price: 7000, recipe: { bodom_sut: 150 } },
        { name: 'Laktozasiz sut', price: 5000, recipe: { laktozasiz_sut: 150 } },
      ],
    },
  ],
  categories: [
    {
      name: 'Kofe',
      icon: '☕',
      station: 'bar',
      modifierGroups: ['sirop'],
      products: [
        { name: 'Espresso', price: 15000, prepMinutes: 2, recipe: { kofe_don: 9, stakan_300: 1 } },
        { name: 'Americano', price: 18000, prepMinutes: 2, recipe: { kofe_don: 9, stakan_300: 1, qopqoq: 1 } },
        {
          name: 'Cappuccino 0.3L',
          price: 25000,
          group: 'Cappuccino',
          variant: '0.3L',
          prepMinutes: 3,
          recipe: { kofe_don: 9, sut: 180, stakan_300: 1, qopqoq: 1 },
          modifierGroups: ['sut_turi'],
        },
        {
          name: 'Cappuccino 0.4L',
          price: 30000,
          group: 'Cappuccino',
          variant: '0.4L',
          prepMinutes: 3,
          recipe: { kofe_don: 18, sut: 240, stakan_400: 1, qopqoq: 1 },
          modifierGroups: ['sut_turi'],
        },
        {
          name: 'Latte 0.3L',
          price: 26000,
          group: 'Latte',
          variant: '0.3L',
          prepMinutes: 3,
          recipe: { kofe_don: 9, sut: 220, stakan_300: 1, qopqoq: 1 },
          modifierGroups: ['sut_turi'],
        },
        {
          name: 'Latte 0.4L',
          price: 31000,
          group: 'Latte',
          variant: '0.4L',
          prepMinutes: 3,
          recipe: { kofe_don: 18, sut: 300, stakan_400: 1, qopqoq: 1 },
          modifierGroups: ['sut_turi'],
        },
        { name: 'Raf', price: 32000, prepMinutes: 4, recipe: { kofe_don: 9, qaymoq: 150, shakar: 10, stakan_300: 1, qopqoq: 1 } },
        {
          name: 'Flat white',
          price: 28000,
          prepMinutes: 3,
          recipe: { kofe_don: 18, sut: 150, stakan_300: 1, qopqoq: 1 },
          modifierGroups: ['sut_turi'],
        },
        { name: 'Kakao', price: 24000, prepMinutes: 3, recipe: { kakao: 20, sut: 250, shakar: 10, stakan_300: 1, qopqoq: 1 }, modifierGroups: ['sut_turi'] },
        {
          name: 'Ays latte',
          price: 30000,
          prepMinutes: 3,
          recipe: { kofe_don: 18, sut: 200, muz: 120, stakan_400: 1, qopqoq: 1 },
          modifierGroups: ['sut_turi'],
        },
      ],
    },
    {
      name: 'Choy',
      icon: '🍵',
      station: 'bar',
      products: [
        { name: 'Ko‘k choy', price: 10000, prepMinutes: 2, recipe: { choy_kok: 4, stakan_400: 1 } },
        { name: 'Qora choy', price: 10000, prepMinutes: 2, recipe: { choy_qora: 4, stakan_400: 1 } },
      ],
    },
    {
      name: 'Desertlar',
      icon: '🍰',
      products: [
        { name: 'Kruassan', price: 18000, prepMinutes: 1, recipe: { kruassan: 1 } },
        { name: 'Chizkeyk', price: 28000, prepMinutes: 1, recipe: { chizkeyk: 1 } },
        { name: 'Medovik', price: 24000, prepMinutes: 1, recipe: { medovik: 1 } },
        { name: 'Brauni', price: 22000, prepMinutes: 1, recipe: { brauni: 1 } },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// KAFE VA RESTORAN (milliy taomlar)
// ---------------------------------------------------------------------------
const CAFE: StarterContent = {
  key: 'cafe',
  label: 'Kafe menyusi',
  rawMaterials: [
    { code: 'mol_goshti', name: 'Mol go‘shti', category: 'go‘sht', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 10000 },
    { code: 'qoy_goshti', name: 'Qo‘y go‘shti', category: 'go‘sht', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 5000 },
    { code: 'tovuq_goshti', name: 'Tovuq go‘shti', category: 'go‘sht', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 5000 },
    { code: 'guruch', name: 'Guruch', category: 'don', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 10000 },
    { code: 'sabzi', name: 'Sabzi', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 5000 },
    { code: 'piyoz', name: 'Piyoz', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 3000 },
    { code: 'kartoshka', name: 'Kartoshka', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 5000 },
    { code: 'pomidor', name: 'Pomidor', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 3000 },
    { code: 'bodring', name: 'Bodring', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 3000 },
    { code: 'yog', name: 'O‘simlik yog‘i', category: 'boshqa', baseUnit: 'ml', packName: 'litr', packSize: 1000, minStock: 5000 },
    { code: 'tandir_non', name: 'Tandir non', category: 'non', baseUnit: 'dona', minStock: 20 },
    { code: 'choy_kok', name: 'Ko‘k choy', category: 'choy', baseUnit: 'g', packName: 'pachka', packSize: 100, minStock: 300 },
    { code: 'limon', name: 'Limon', category: 'meva', baseUnit: 'dona', minStock: 10 },
    { code: 'cola_1', name: 'Coca-Cola 1L', category: 'ichimlik', baseUnit: 'dona', packName: 'blok', packSize: 6, minStock: 12 },
    { code: 'medovik', name: 'Medovik (bo‘lak)', category: 'desert', baseUnit: 'dona', minStock: 8 },
  ],
  modifierGroups: [],
  categories: [
    {
      name: 'Salatlar',
      icon: '🥗',
      station: 'kitchen',
      products: [
        { name: 'Achchiq-chuchuk', price: 18000, prepMinutes: 5, recipe: { pomidor: 150, bodring: 100, piyoz: 30 } },
        { name: 'Bahor salati', price: 22000, prepMinutes: 5, recipe: { pomidor: 120, bodring: 120, yog: 10 } },
      ],
    },
    {
      name: 'Sho‘rvalar',
      icon: '🍲',
      station: 'kitchen',
      products: [
        { name: 'Mastava', price: 28000, prepMinutes: 5, recipe: { mol_goshti: 80, guruch: 50, kartoshka: 80, sabzi: 40, piyoz: 20 } },
        { name: 'Sho‘rva', price: 32000, prepMinutes: 5, recipe: { qoy_goshti: 120, kartoshka: 120, sabzi: 50, piyoz: 20 } },
      ],
    },
    {
      name: 'Asosiy taomlar',
      icon: '🍛',
      station: 'kitchen',
      products: [
        { name: 'Osh', price: 45000, prepMinutes: 3, recipe: { mol_goshti: 120, guruch: 150, sabzi: 120, piyoz: 30, yog: 40 } },
        { name: 'Qozon kabob', price: 65000, prepMinutes: 15, recipe: { qoy_goshti: 250, kartoshka: 200, piyoz: 40, yog: 30 } },
        { name: 'Tovuq kabob', price: 48000, prepMinutes: 15, recipe: { tovuq_goshti: 250, piyoz: 30 } },
      ],
    },
    {
      name: 'Non',
      icon: '🫓',
      products: [{ name: 'Tandir non', price: 6000, prepMinutes: 0, recipe: { tandir_non: 1 } }],
    },
    {
      name: 'Ichimliklar',
      icon: '🫖',
      station: 'bar',
      products: [
        { name: 'Ko‘k choy (choynak)', shortName: 'Choynak', price: 8000, prepMinutes: 2, recipe: { choy_kok: 6 } },
        { name: 'Limonli choy', price: 15000, prepMinutes: 2, recipe: { choy_kok: 6, limon: 1 } },
        { name: 'Coca-Cola 1L', price: 14000, prepMinutes: 0, recipe: { cola_1: 1 } },
      ],
    },
    {
      name: 'Desertlar',
      icon: '🍰',
      products: [{ name: 'Medovik', price: 22000, prepMinutes: 1, recipe: { medovik: 1 } }],
    },
  ],
};

// ---------------------------------------------------------------------------
// MILLIY RESTORAN / CHOYXONA (Q-055): zal, keyin to'lov, oshxona va mangal
// ---------------------------------------------------------------------------
const NATIONAL: StarterContent = {
  key: 'national',
  label: 'Milliy taomlar menyusi',
  rawMaterials: [
    { code: 'mol_goshti', name: 'Mol go‘shti', category: 'go‘sht', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 10000 },
    { code: 'qoy_goshti', name: 'Qo‘y go‘shti', category: 'go‘sht', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 10000 },
    { code: 'tovuq_goshti', name: 'Tovuq go‘shti', category: 'go‘sht', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 5000 },
    { code: 'jigar', name: 'Jigar', category: 'go‘sht', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 2000 },
    { code: 'qiyma', name: 'Qiyma', category: 'go‘sht', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 3000 },
    { code: 'dumba', name: 'Dumba yog‘i', category: 'go‘sht', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 1000 },
    { code: 'guruch', name: 'Guruch', category: 'don', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 20000 },
    { code: 'un', name: 'Un', category: 'don', baseUnit: 'g', packName: 'qop', packSize: 50000, minStock: 25000 },
    { code: 'mosh', name: 'Mosh', category: 'don', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 2000 },
    { code: 'noxat', name: 'No‘xat', category: 'don', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 2000 },
    { code: 'sabzi', name: 'Sabzi', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 10000 },
    { code: 'piyoz', name: 'Piyoz', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 5000 },
    { code: 'kartoshka', name: 'Kartoshka', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 5000 },
    { code: 'pomidor', name: 'Pomidor', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 3000 },
    { code: 'bodring', name: 'Bodring', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 3000 },
    { code: 'bulgor_qalampir', name: 'Bulg‘or qalampiri', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 1000 },
    { code: 'kokat', name: 'Ko‘kat', category: 'sabzavot', baseUnit: 'g', packName: 'bog‘', packSize: 100, minStock: 500 },
    { code: 'yog', name: 'O‘simlik yog‘i', category: 'boshqa', baseUnit: 'ml', packName: 'litr', packSize: 1000, minStock: 10000 },
    { code: 'katiq', name: 'Qatiq', category: 'sut', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 2000 },
    { code: 'tandir_non', name: 'Tandir non', category: 'non', baseUnit: 'dona', minStock: 30 },
    { code: 'choy_kok', name: 'Ko‘k choy', category: 'choy', baseUnit: 'g', packName: 'pachka', packSize: 100, minStock: 500 },
    { code: 'choy_qora', name: 'Qora choy', category: 'choy', baseUnit: 'g', packName: 'pachka', packSize: 100, minStock: 500 },
    { code: 'limon', name: 'Limon', category: 'meva', baseUnit: 'dona', minStock: 10 },
    { code: 'kompot', name: 'Kompot (tayyorlangan)', category: 'ichimlik', baseUnit: 'ml', packName: 'litr', packSize: 1000, minStock: 5000 },
    { code: 'cola_1', name: 'Coca-Cola 1L', category: 'ichimlik', baseUnit: 'dona', packName: 'blok', packSize: 6, minStock: 12 },
    { code: 'suv_05', name: 'Suv 0.5L', category: 'ichimlik', baseUnit: 'dona', packName: 'blok', packSize: 12, minStock: 24 },
    { code: 'medovik', name: 'Medovik (bo‘lak)', category: 'desert', baseUnit: 'dona', minStock: 8 },
  ],
  modifierGroups: [
    {
      key: 'non',
      name: 'Non',
      minSelect: 0,
      maxSelect: 2,
      modifiers: [{ name: 'Tandir non', price: 6000, recipe: { tandir_non: 1 } }],
    },
  ],
  categories: [
    {
      name: 'Salatlar',
      icon: '🥗',
      station: 'kitchen',
      products: [
        { name: 'Achchiq-chuchuk', price: 18000, prepMinutes: 5, recipe: { pomidor: 150, bodring: 100, piyoz: 30 } },
        { name: 'Bahor salati', price: 22000, prepMinutes: 5, recipe: { pomidor: 120, bodring: 120, kokat: 10, yog: 10 } },
        { name: 'Qatiq', price: 10000, prepMinutes: 1, recipe: { katiq: 200 } },
      ],
    },
    {
      name: 'Sho‘rvalar',
      icon: '🍲',
      station: 'kitchen',
      modifierGroups: ['non'],
      products: [
        { name: 'Sho‘rva', price: 35000, prepMinutes: 5, recipe: { qoy_goshti: 150, kartoshka: 120, sabzi: 60, piyoz: 30, noxat: 20 } },
        { name: 'Mastava', price: 28000, prepMinutes: 5, recipe: { mol_goshti: 80, guruch: 50, kartoshka: 80, sabzi: 40, piyoz: 20 } },
        { name: 'Moshxo‘rda', price: 28000, prepMinutes: 5, recipe: { mol_goshti: 80, mosh: 60, guruch: 40, sabzi: 40, piyoz: 20 } },
        { name: 'Chuchvara', price: 30000, prepMinutes: 7, recipe: { qiyma: 120, un: 80, piyoz: 20 } },
      ],
    },
    {
      name: 'Milliy taomlar',
      icon: '🍛',
      station: 'kitchen',
      modifierGroups: ['non'],
      products: [
        { name: 'Osh', price: 45000, group: 'Osh', variant: '1 porsiya', prepMinutes: 3, recipe: { mol_goshti: 120, guruch: 150, sabzi: 120, piyoz: 30, yog: 40, noxat: 15 } },
        { name: 'Osh (yarim)', price: 30000, group: 'Osh', variant: 'Yarim', prepMinutes: 3, recipe: { mol_goshti: 60, guruch: 80, sabzi: 60, piyoz: 15, yog: 20 } },
        { name: 'Lag‘mon', price: 38000, prepMinutes: 8, recipe: { mol_goshti: 100, un: 150, bulgor_qalampir: 40, pomidor: 50, piyoz: 20, yog: 15 } },
        { name: 'Manti (5 dona)', price: 35000, prepMinutes: 5, recipe: { qiyma: 200, un: 150, piyoz: 60, dumba: 20 } },
        { name: 'Qozon kabob', price: 65000, prepMinutes: 15, recipe: { qoy_goshti: 250, kartoshka: 200, piyoz: 40, yog: 30 } },
        { name: 'Dimlama', price: 48000, prepMinutes: 10, recipe: { mol_goshti: 150, kartoshka: 150, sabzi: 80, bulgor_qalampir: 50, piyoz: 40 } },
      ],
    },
    {
      name: 'Shashliklar',
      icon: '🍢',
      station: 'mangal',
      modifierGroups: ['non'],
      products: [
        { name: 'Shashlik mol go‘shti', price: 22000, prepMinutes: 15, recipe: { mol_goshti: 120, piyoz: 20 } },
        { name: 'Shashlik qo‘y go‘shti', price: 25000, prepMinutes: 15, recipe: { qoy_goshti: 120, dumba: 15, piyoz: 20 } },
        { name: 'Shashlik tovuq', price: 18000, prepMinutes: 15, recipe: { tovuq_goshti: 130, piyoz: 20 } },
        { name: 'Shashlik jigar', price: 18000, prepMinutes: 12, recipe: { jigar: 120, dumba: 15 } },
        { name: 'Lyulya kabob', price: 20000, prepMinutes: 12, recipe: { qiyma: 130, piyoz: 20 } },
      ],
    },
    {
      name: 'Non va somsa',
      icon: '🫓',
      station: 'mangal',
      products: [
        { name: 'Tandir non', price: 6000, prepMinutes: 0, recipe: { tandir_non: 1 } },
        { name: 'Tandir somsa', price: 12000, prepMinutes: 3, recipe: { qiyma: 70, un: 80, piyoz: 30, dumba: 10 } },
      ],
    },
    {
      name: 'Choy va ichimliklar',
      icon: '🫖',
      products: [
        { name: 'Ko‘k choy (choynak)', shortName: 'Choynak ko‘k', price: 8000, prepMinutes: 2, recipe: { choy_kok: 6 } },
        { name: 'Qora choy (choynak)', shortName: 'Choynak qora', price: 8000, prepMinutes: 2, recipe: { choy_qora: 6 } },
        { name: 'Limonli choy', price: 15000, prepMinutes: 2, recipe: { choy_kok: 6, limon: 1 } },
        { name: 'Kompot (1 L)', price: 15000, prepMinutes: 0, recipe: { kompot: 1000 } },
        { name: 'Coca-Cola 1L', price: 14000, prepMinutes: 0, recipe: { cola_1: 1 } },
        { name: 'Suv 0.5L', price: 4000, prepMinutes: 0, recipe: { suv_05: 1 } },
      ],
    },
    {
      name: 'Desertlar',
      icon: '🍰',
      products: [{ name: 'Medovik', price: 22000, prepMinutes: 1, recipe: { medovik: 1 } }],
    },
  ],
};

// ---------------------------------------------------------------------------
// OSHXONA (STOLOVAYA) (Q-055): tayyor taomlar, kassada avval to'lov
// ---------------------------------------------------------------------------
const CANTEEN: StarterContent = {
  key: 'canteen',
  label: 'Oshxona menyusi',
  rawMaterials: [
    { code: 'mol_goshti', name: 'Mol go‘shti', category: 'go‘sht', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 10000 },
    { code: 'tovuq_goshti', name: 'Tovuq go‘shti', category: 'go‘sht', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 5000 },
    { code: 'qiyma', name: 'Qiyma', category: 'go‘sht', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 3000 },
    { code: 'guruch', name: 'Guruch', category: 'don', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 10000 },
    { code: 'grechka', name: 'Grechka', category: 'don', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 5000 },
    { code: 'makaron', name: 'Makaron', category: 'don', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 5000 },
    { code: 'mosh', name: 'Mosh', category: 'don', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 2000 },
    { code: 'kartoshka', name: 'Kartoshka', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 10000 },
    { code: 'sabzi', name: 'Sabzi', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 5000 },
    { code: 'piyoz', name: 'Piyoz', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 3000 },
    { code: 'pomidor', name: 'Pomidor', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 3000 },
    { code: 'bodring', name: 'Bodring', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 3000 },
    { code: 'karam', name: 'Karam', category: 'sabzavot', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 3000 },
    { code: 'sut', name: 'Sut', category: 'sut', baseUnit: 'ml', packName: 'litr', packSize: 1000, minStock: 5000 },
    { code: 'saryog', name: 'Saryog‘', category: 'sut', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 1000 },
    { code: 'yog', name: 'O‘simlik yog‘i', category: 'boshqa', baseUnit: 'ml', packName: 'litr', packSize: 1000, minStock: 5000 },
    { code: 'tandir_non', name: 'Non', category: 'non', baseUnit: 'dona', minStock: 30 },
    { code: 'choy_quruq', name: 'Choy (quruq)', category: 'choy', baseUnit: 'g', packName: 'pachka', packSize: 100, minStock: 300 },
    { code: 'kompot', name: 'Kompot (tayyorlangan)', category: 'ichimlik', baseUnit: 'ml', packName: 'litr', packSize: 1000, minStock: 5000 },
  ],
  modifierGroups: [],
  categories: [
    {
      name: 'Birinchi taomlar',
      icon: '🍲',
      products: [
        { name: 'Sho‘rva', price: 22000, prepMinutes: 1, recipe: { mol_goshti: 80, kartoshka: 100, sabzi: 40, piyoz: 20 } },
        { name: 'Mastava', price: 18000, prepMinutes: 1, recipe: { mol_goshti: 50, guruch: 40, kartoshka: 60, sabzi: 30 } },
        { name: 'Moshxo‘rda', price: 18000, prepMinutes: 1, recipe: { mol_goshti: 50, mosh: 50, guruch: 30, sabzi: 30 } },
      ],
    },
    {
      name: 'Ikkinchi taomlar',
      icon: '🍛',
      products: [
        { name: 'Osh', price: 30000, prepMinutes: 1, recipe: { mol_goshti: 80, guruch: 130, sabzi: 100, piyoz: 20, yog: 30 } },
        { name: 'Grechka go‘sht bilan', price: 25000, prepMinutes: 1, recipe: { mol_goshti: 80, grechka: 100, piyoz: 20, saryog: 10 } },
        { name: 'Makaron go‘sht bilan', price: 23000, prepMinutes: 1, recipe: { mol_goshti: 80, makaron: 100, saryog: 10 } },
        { name: 'Kotlet pyure bilan', price: 25000, prepMinutes: 1, recipe: { qiyma: 120, kartoshka: 200, sut: 50, saryog: 10 } },
        { name: 'Tovuq garnir bilan', price: 26000, prepMinutes: 1, recipe: { tovuq_goshti: 150, guruch: 100 } },
      ],
    },
    {
      name: 'Salatlar',
      icon: '🥗',
      products: [
        { name: 'Achchiq-chuchuk', price: 10000, prepMinutes: 1, recipe: { pomidor: 100, bodring: 70, piyoz: 20 } },
        { name: 'Karam salati', price: 8000, prepMinutes: 1, recipe: { karam: 120, sabzi: 30, yog: 5 } },
      ],
    },
    {
      name: 'Non va ichimliklar',
      icon: '🫖',
      products: [
        { name: 'Non', price: 4000, prepMinutes: 0, recipe: { tandir_non: 1 } },
        { name: 'Choy', price: 3000, prepMinutes: 0, recipe: { choy_quruq: 3 } },
        { name: 'Kompot', price: 5000, prepMinutes: 0, recipe: { kompot: 250 } },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// BAR / LOUNGE (Q-055): ichimliklar ml bo'yicha (shisha — ml), kalyan, gazaklar
// ---------------------------------------------------------------------------
const BAR_MATERIALS: StarterRawMaterial[] = [
  { code: 'pivo_bochka', name: 'Pivo (bochka)', category: 'pivo', baseUnit: 'ml', packName: 'bochka', packSize: 30000, minStock: 30000 },
  { code: 'pivo_shisha', name: 'Pivo 0.5L (shisha)', category: 'pivo', baseUnit: 'dona', packName: 'yashik', packSize: 20, minStock: 20 },
  { code: 'aroq', name: 'Aroq', category: 'kuchli', baseUnit: 'ml', packName: 'shisha', packSize: 500, minStock: 2500 },
  { code: 'konyak', name: 'Konyak', category: 'kuchli', baseUnit: 'ml', packName: 'shisha', packSize: 500, minStock: 1500 },
  { code: 'viski', name: 'Viski', category: 'kuchli', baseUnit: 'ml', packName: 'shisha', packSize: 700, minStock: 1400 },
  { code: 'rom', name: 'Rom', category: 'kuchli', baseUnit: 'ml', packName: 'shisha', packSize: 700, minStock: 700 },
  { code: 'jin', name: 'Jin', category: 'kuchli', baseUnit: 'ml', packName: 'shisha', packSize: 700, minStock: 700 },
  { code: 'vino_qizil', name: 'Vino (qizil)', category: 'vino', baseUnit: 'ml', packName: 'shisha', packSize: 750, minStock: 1500 },
  { code: 'vino_oq', name: 'Vino (oq)', category: 'vino', baseUnit: 'ml', packName: 'shisha', packSize: 750, minStock: 1500 },
  { code: 'tonik', name: 'Tonik', category: 'alkogolsiz', baseUnit: 'ml', packName: 'shisha', packSize: 1000, minStock: 2000 },
  { code: 'cola_ml', name: 'Coca-Cola (kokteyl uchun)', category: 'alkogolsiz', baseUnit: 'ml', packName: 'shisha', packSize: 1500, minStock: 3000 },
  { code: 'sharbat', name: 'Sharbat', category: 'alkogolsiz', baseUnit: 'ml', packName: 'litr', packSize: 1000, minStock: 3000 },
  { code: 'cola_05', name: 'Coca-Cola 0.5L', category: 'alkogolsiz', baseUnit: 'dona', packName: 'blok', packSize: 12, minStock: 24 },
  { code: 'suv_05', name: 'Suv 0.5L', category: 'alkogolsiz', baseUnit: 'dona', packName: 'blok', packSize: 12, minStock: 24 },
  { code: 'energetik', name: 'Energetik', category: 'alkogolsiz', baseUnit: 'dona', packName: 'blok', packSize: 12, minStock: 12 },
  { code: 'limon', name: 'Limon', category: 'meva', baseUnit: 'dona', minStock: 10 },
  { code: 'laym', name: 'Laym', category: 'meva', baseUnit: 'dona', minStock: 10 },
  { code: 'yalpiz', name: 'Yalpiz', category: 'meva', baseUnit: 'g', minStock: 200 },
  { code: 'shakar_sirop', name: 'Shakar siropi', category: 'sirop', baseUnit: 'ml', packName: 'shisha', packSize: 1000, minStock: 500 },
  { code: 'muz', name: 'Muz', category: 'boshqa', baseUnit: 'g', packName: 'qop', packSize: 5000, minStock: 10000 },
  { code: 'kalyan_tamaki', name: 'Kalyan tamakisi', category: 'kalyan', baseUnit: 'g', packName: 'pachka', packSize: 250, minStock: 500 },
  { code: 'kalyan_kumir', name: 'Kalyan ko‘miri', category: 'kalyan', baseUnit: 'dona', packName: 'quti', packSize: 72, minStock: 72 },
  { code: 'kartoshka_fri', name: 'Kartoshka fri (muzlatilgan)', category: 'gazak', baseUnit: 'g', packName: 'qop', packSize: 2500, minStock: 5000 },
  { code: 'tovuq_qanot', name: 'Tovuq qanotlari', category: 'gazak', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 3000 },
  { code: 'pishloq', name: 'Pishloq', category: 'gazak', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 1000 },
  { code: 'yongoq', name: 'Yong‘oq (tuzlangan)', category: 'gazak', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 1000 },
  { code: 'meva_assorti', name: 'Mevalar (assorti)', category: 'meva', baseUnit: 'g', packName: 'kg', packSize: 1000, minStock: 2000 },
  { code: 'yog_fri', name: 'Yog‘ (fri uchun)', category: 'gazak', baseUnit: 'ml', packName: 'litr', packSize: 1000, minStock: 5000 },
];

const BAR_CATEGORIES: StarterCategory[] = [
  {
    name: 'Pivo',
    icon: '🍺',
    products: [
      { name: 'Pivo bochka 0.5L', price: 25000, group: 'Pivo bochka', variant: '0.5L', prepMinutes: 1, recipe: { pivo_bochka: 500 } },
      { name: 'Pivo bochka 1L', price: 45000, group: 'Pivo bochka', variant: '1L', prepMinutes: 1, recipe: { pivo_bochka: 1000 } },
      { name: 'Pivo shisha 0.5L', price: 22000, prepMinutes: 0, recipe: { pivo_shisha: 1 } },
    ],
  },
  {
    name: 'Kuchli ichimliklar',
    icon: '🥃',
    products: [
      { name: 'Aroq 50 ml', price: 15000, group: 'Aroq', variant: '50 ml', prepMinutes: 0, recipe: { aroq: 50 } },
      { name: 'Aroq 500 ml', price: 120000, group: 'Aroq', variant: '500 ml', prepMinutes: 0, recipe: { aroq: 500 } },
      { name: 'Konyak 50 ml', price: 30000, prepMinutes: 0, recipe: { konyak: 50 } },
      { name: 'Viski 50 ml', price: 45000, prepMinutes: 0, recipe: { viski: 50 } },
    ],
  },
  {
    name: 'Vino',
    icon: '🍷',
    products: [
      { name: 'Vino qizil (bokal)', price: 35000, group: 'Vino qizil', variant: 'Bokal', prepMinutes: 0, recipe: { vino_qizil: 150 } },
      { name: 'Vino qizil (shisha)', price: 180000, group: 'Vino qizil', variant: 'Shisha', prepMinutes: 0, recipe: { vino_qizil: 750 } },
      { name: 'Vino oq (bokal)', price: 35000, group: 'Vino oq', variant: 'Bokal', prepMinutes: 0, recipe: { vino_oq: 150 } },
      { name: 'Vino oq (shisha)', price: 180000, group: 'Vino oq', variant: 'Shisha', prepMinutes: 0, recipe: { vino_oq: 750 } },
    ],
  },
  {
    name: 'Kokteyllar',
    icon: '🍹',
    products: [
      { name: 'Moxito', price: 45000, prepMinutes: 4, recipe: { rom: 50, laym: 1, yalpiz: 10, shakar_sirop: 20, muz: 150 } },
      { name: 'Moxito (alkogolsiz)', price: 32000, prepMinutes: 4, recipe: { laym: 1, yalpiz: 10, shakar_sirop: 20, tonik: 150, muz: 150 } },
      { name: 'Jin-tonik', price: 42000, prepMinutes: 2, recipe: { jin: 50, tonik: 150, limon: 1, muz: 100 } },
      { name: 'Viski-kola', price: 55000, prepMinutes: 2, recipe: { viski: 50, cola_ml: 150, muz: 100 } },
    ],
  },
  {
    name: 'Alkogolsiz',
    icon: '🥤',
    products: [
      { name: 'Coca-Cola 0.5L', price: 12000, prepMinutes: 0, recipe: { cola_05: 1 } },
      { name: 'Suv 0.5L', price: 7000, prepMinutes: 0, recipe: { suv_05: 1 } },
      { name: 'Sharbat (stakan)', price: 15000, prepMinutes: 0, recipe: { sharbat: 250 } },
      { name: 'Energetik', price: 20000, prepMinutes: 0, recipe: { energetik: 1 } },
    ],
  },
  {
    name: 'Gazaklar',
    icon: '🍟',
    station: 'kitchen',
    products: [
      { name: 'Kartoshka fri', price: 25000, prepMinutes: 6, recipe: { kartoshka_fri: 200, yog_fri: 20 } },
      { name: 'Tovuq qanotlari', price: 55000, prepMinutes: 15, recipe: { tovuq_qanot: 400, yog_fri: 30 } },
      { name: 'Pishloq tarelkasi', price: 60000, prepMinutes: 5, recipe: { pishloq: 200 } },
      { name: 'Yong‘oq', price: 20000, prepMinutes: 1, recipe: { yongoq: 100 } },
      { name: 'Meva tarelkasi', price: 70000, prepMinutes: 5, recipe: { meva_assorti: 600 } },
    ],
  },
  {
    name: 'Kalyan',
    icon: '💨',
    products: [
      { name: 'Kalyan klassik', price: 120000, prepMinutes: 10, recipe: { kalyan_tamaki: 20, kalyan_kumir: 3 } },
      { name: 'Kalyan meva ustida', price: 160000, prepMinutes: 15, recipe: { kalyan_tamaki: 20, kalyan_kumir: 3, meva_assorti: 300 } },
      { name: 'Ko‘mir almashtirish', price: 20000, prepMinutes: 2, recipe: { kalyan_kumir: 3 } },
    ],
  },
];

const BAR: StarterContent = {
  key: 'bar',
  label: 'Bar menyusi',
  rawMaterials: BAR_MATERIALS,
  modifierGroups: [],
  categories: BAR_CATEGORIES,
};

// ---------------------------------------------------------------------------
// KLUB (Q-055): bar menyusi + kirish chiptasi va VIP to'plamlar
// ---------------------------------------------------------------------------
const CLUB: StarterContent = {
  key: 'club',
  label: 'Klub menyusi',
  rawMaterials: BAR_MATERIALS,
  modifierGroups: [],
  categories: [
    {
      name: 'Kirish',
      icon: '🎟️',
      products: [
        { name: 'Kirish chiptasi', price: 100000, group: 'Kirish', variant: 'Oddiy', prepMinutes: 0, trackStock: false },
        { name: 'Kirish chiptasi VIP', price: 250000, group: 'Kirish', variant: 'VIP', prepMinutes: 0, trackStock: false },
      ],
    },
    {
      name: 'VIP to‘plamlar',
      icon: '⭐',
      products: [
        { name: 'VIP to‘plam: aroq + mevalar', price: 450000, prepMinutes: 5, recipe: { aroq: 500, meva_assorti: 600, sharbat: 1000 } },
        { name: 'VIP to‘plam: viski + kola', price: 900000, prepMinutes: 5, recipe: { viski: 700, cola_ml: 1500, muz: 500 } },
      ],
    },
    ...BAR_CATEGORIES,
  ],
};

export const STARTER_CONTENTS = [FAST, COFFEE, CAFE, NATIONAL, CANTEEN, BAR, CLUB] as const;

/** Qaysi restoran shabloniga qaysi boshlang'ich menyu mos */
export const STARTER_BY_TEMPLATE: Readonly<Record<RestaurantTemplateKey, StarterContent | null>> = {
  fast_small: FAST,
  fast_kds: FAST,
  coffee: COFFEE,
  national: NATIONAL,
  canteen: CANTEEN,
  bar: BAR,
  club: CLUB,
  cafe_restaurant: CAFE,
  mixed: CAFE,
  blank: null,
};

export function starterForTemplate(key: string): StarterContent | null {
  return (STARTER_BY_TEMPLATE as Readonly<Record<string, StarterContent | null>>)[key] ?? null;
}

/** Kontentdagi ichki xatolar (noma'lum xom ashyo, takror nom va h.k.). Bo'sh = joyida. */
export function validateStarterContent(c: StarterContent): string[] {
  const errors: string[] = [];
  const raw = new Set<string>();
  for (const r of c.rawMaterials) {
    if (!/^[a-z0-9_]+$/.test(r.code)) errors.push(`${c.key}: xom ashyo kodi noto‘g‘ri: ${r.code}`);
    if (raw.has(r.code)) errors.push(`${c.key}: xom ashyo takrorlangan: ${r.code}`);
    raw.add(r.code);
  }
  const checkRecipe = (where: string, recipe?: StarterRecipe) => {
    for (const [code, qty] of Object.entries(recipe ?? {})) {
      if (!raw.has(code)) errors.push(`${c.key}: ${where} retseptida noma’lum xom ashyo: ${code}`);
      if (!(qty > 0)) errors.push(`${c.key}: ${where} retseptida miqdor musbat bo‘lishi kerak: ${code}`);
    }
  };
  const groups = new Set<string>();
  for (const g of c.modifierGroups) {
    if (groups.has(g.key)) errors.push(`${c.key}: qo‘shimcha guruhi takrorlangan: ${g.key}`);
    groups.add(g.key);
    if (g.maxSelect < g.minSelect) errors.push(`${c.key}: ${g.name}: eng ko‘p tanlov eng kamdan kichik`);
    for (const m of g.modifiers) {
      if (!Number.isInteger(m.price) || m.price < 0) errors.push(`${c.key}: ${m.name}: narx noto‘g‘ri`);
      checkRecipe(m.name, m.recipe);
    }
  }
  const names = new Set<string>();
  for (const cat of c.categories) {
    for (const g of cat.modifierGroups ?? []) {
      if (!groups.has(g)) errors.push(`${c.key}: ${cat.name}: noma’lum qo‘shimcha guruhi ${g}`);
    }
    for (const p of cat.products) {
      if (names.has(p.name)) errors.push(`${c.key}: mahsulot nomi takrorlangan: ${p.name}`);
      names.add(p.name);
      if (!Number.isInteger(p.price) || p.price <= 0) errors.push(`${c.key}: ${p.name}: narx noto‘g‘ri`);
      checkRecipe(p.name, p.recipe);
      for (const g of p.modifierGroups ?? []) {
        if (!groups.has(g)) errors.push(`${c.key}: ${p.name}: noma’lum qo‘shimcha guruhi ${g}`);
      }
    }
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Shablon → menyu kutubxonasi (Q-054): shablondan ochilgan restoranda taom, guruh va kategoriya rasmlari
// kutubxonadan o'zi qo'yiladi (admin kutubxona rasmini almashtirsa — ularda ham yangilanadi, ega o'zinikini qo'ymaguncha).
// Kalitlar: shablon nomi → kutubxona taomi kaliti / kutubxona kategoriya kaliti. Mosi yo'qlari — rasmsiz.
// ---------------------------------------------------------------------------
export interface StarterLibraryMap {
  categories: Readonly<Record<string, string>>;
  products: Readonly<Record<string, string>>;
}

const BAR_LIBRARY: StarterLibraryMap = {
  categories: { Pivo: 'pivo', Kokteyllar: 'kokteyl', Kalyan: 'kalyan', Gazaklar: 'gazak', Alkogolsiz: 'sovuq' },
  products: {
    'Pivo bochka 0.5L': 'pivo_bochka_05',
    'Pivo bochka 1L': 'pivo_bochka_1',
    'Pivo shisha 0.5L': 'pivo_shisha',
    Moxito: 'kokteyl_moxito',
    'Moxito (alkogolsiz)': 'moxito',
    'Jin-tonik': 'kokteyl_jin_tonik',
    'Viski-kola': 'kokteyl_viski_kola',
    'Kalyan klassik': 'kalyan_klassik',
    'Kalyan meva ustida': 'kalyan_meva',
    'Kartoshka fri': 'fri_standart',
    'Coca-Cola 0.5L': 'cola_05',
    'Suv 0.5L': 'suv_05',
    Energetik: 'energetik',
  },
};

export const STARTER_LIBRARY: Readonly<Record<string, StarterLibraryMap>> = {
  fast: {
    categories: { Lavash: 'lavash', Shaurma: 'shaurma', 'Burger va hot-dog': 'burger', Garnir: 'gazak', Ichimliklar: 'sovuq' },
    products: {
      'Lavash mol go‘shtli': 'lavash_mol_standart',
      'Lavash tovuqli': 'lavash_tovuq_standart',
      'Lavash pishloqli': 'lavash_pishloq_standart',
      'Shaurma mol go‘shtli': 'shaurma_mol_standart',
      'Shaurma tovuqli': 'shaurma_tovuq_standart',
      Gamburger: 'gamburger',
      Chizburger: 'chizburger',
      'Hot-dog': 'hotdog_1',
      'Hot-dog dubl': 'hotdog_2',
      'Kartoshka fri': 'fri_standart',
      'Kartoshka fri katta': 'fri_katta',
      'Coca-Cola 0.5L': 'cola_05',
      'Coca-Cola 1.5L': 'cola_15',
      'Fanta 0.5L': 'fanta_05',
      'Suv 0.5L': 'suv_05',
      Choy: 'choy_qora',
    },
  },
  coffee: {
    categories: { Kofe: 'issiq', Choy: 'issiq', Desertlar: 'desert' },
    products: {
      Americano: 'amerikano',
      'Cappuccino 0.3L': 'kapuchino',
      'Cappuccino 0.4L': 'kapuchino',
      'Latte 0.3L': 'latte',
      'Latte 0.4L': 'latte',
      'Ko‘k choy': 'choy_kok',
      'Qora choy': 'choy_qora',
      Chizkeyk: 'chizkeyk',
      Kakao: 'kakao',
    },
  },
  national: {
    categories: {
      Salatlar: 'salat',
      'Sho‘rvalar': 'shorva',
      'Milliy taomlar': 'milliy',
      Shashliklar: 'shashlik',
      'Non va somsa': 'non_somsa',
      'Choy va ichimliklar': 'issiq',
      Desertlar: 'desert',
    },
    products: {
      'Achchiq-chuchuk': 'achchiq_chuchuk',
      'Sho‘rva': 'shorva',
      Mastava: 'mastava',
      'Moshxo‘rda': 'moshxorda',
      Chuchvara: 'chuchvara',
      Osh: 'osh_1',
      'Osh (yarim)': 'osh_yarim',
      'Lag‘mon': 'lagmon',
      'Manti (5 dona)': 'manti',
      'Qozon kabob': 'qozon_kabob',
      Dimlama: 'dimlama',
      'Shashlik mol go‘shti': 'shashlik_mol',
      'Shashlik qo‘y go‘shti': 'shashlik_qoy',
      'Shashlik tovuq': 'shashlik_tovuq',
      'Shashlik jigar': 'shashlik_jigar',
      'Lyulya kabob': 'lyulya',
      'Tandir non': 'non_tandir',
      'Tandir somsa': 'somsa',
      'Ko‘k choy (choynak)': 'choy_kok',
      'Qora choy (choynak)': 'choy_qora',
      'Limonli choy': 'choy_limon',
      'Kompot (1 L)': 'kompot',
      'Coca-Cola 1L': 'cola_1',
      'Suv 0.5L': 'suv_05',
    },
  },
  canteen: {
    categories: { 'Birinchi taomlar': 'shorva', 'Ikkinchi taomlar': 'milliy', Salatlar: 'salat' },
    products: {
      'Sho‘rva': 'shorva',
      Mastava: 'mastava',
      'Moshxo‘rda': 'moshxorda',
      Osh: 'osh_1',
      'Achchiq-chuchuk': 'achchiq_chuchuk',
      Non: 'non_tandir',
      Kompot: 'kompot',
    },
  },
  bar: BAR_LIBRARY,
  club: BAR_LIBRARY,
  cafe: {
    categories: { Salatlar: 'salat', Desertlar: 'desert' },
    products: {
      'Achchiq-chuchuk': 'achchiq_chuchuk',
      'Ko‘k choy (choynak)': 'choy_kok',
      'Limonli choy': 'choy_limon',
      'Coca-Cola 1L': 'cola_1',
    },
  },
};
